import { jsx } from '@emotion/react/jsx-runtime';
import { createPolymorphicComponent, useSlot, DisabledEffect, FocusRing } from '@nettbureau/core';
import { useCss, inset } from '@nettbureau/styling';
import { mergeProps } from 'react-aria';
import { Text } from './text.js';
import { useLink } from './use-link.js';

/**
 * A component to create stylized anchor elements. It is an extension of the text component with
 * custom styles.
 */ const Link = createPolymorphicComponent((props, ref)=>{
    const [mergedProps, mergedRef] = useSlot('Link', props, ref);
    const { as = 'a', autoFocus, children, className, color = 'accent', colorTone = 'medium', css, disabled, href, onBlur, onFocus, onFocusChange, onHoverChange, onHoverEnd, onHoverStart, onKeyDown, onKeyUp, onPress, onPressChange, onPressEnd, onPressStart, onPressUp, variant = 'underlined', ...textProps } = mergedProps;
    const [{ focused, focusVisible, hovered }, { linkProps }] = useLink({
        ...mergedProps,
        as
    }, mergedRef);
    const linkCss = useCss(({ mixins })=>[
            {
                cursor: 'pointer',
                textDecoration: 'none'
            },
            mixins.transition({
                properties: [
                    'border',
                    'color'
                ]
            }),
            variant === 'underlined' && mixins.underline({
                underline: color === 'inherit' ? 'neutral' : color,
                underlineTone: focusVisible ? 'none' : focused || hovered ? 'strong' : 'weak'
            }),
            variant === 'soft' && [
                mixins.spacing({
                    padding: {
                        x: '2xs'
                    }
                }),
                {
                    position: 'relative',
                    '&::before': [
                        inset(0),
                        mixins.borderRadius({
                            radius: '2xs'
                        }),
                        mixins.fill({
                            fill: color === 'inherit' ? 'neutral' : color,
                            fillTone: 'soft'
                        }),
                        {
                            content: '""',
                            position: 'absolute'
                        }
                    ]
                }
            ],
            (focused || hovered) && !focusVisible && [
                mixins.typography({
                    color: 'neutral'
                })
            ],
            focusVisible && mixins.borderRadius({
                radius: '2xs'
            })
        ]);
    return /*#__PURE__*/ jsx(DisabledEffect, {
        disabled: disabled,
        children: /*#__PURE__*/ jsx(FocusRing, {
            focused: focusVisible,
            children: /*#__PURE__*/ jsx(Text, {
                ...mergeProps(linkProps, textProps),
                ref: mergedRef,
                as: as,
                className: className,
                css: [
                    linkCss,
                    css
                ],
                color: color,
                colorTone: colorTone,
                children: children
            })
        })
    });
});

export { Link };
//# sourceMappingURL=link.js.map
